import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  copied: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, copied: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          p: 4,
        }}
      >
        <Typography variant="h5" color="error" fontWeight="bold">
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, textAlign: "center" }}>
          {error.message}
        </Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: "grey.100",
            color: "error.dark",
            p: 2,
            borderRadius: 1,
            maxWidth: 800,
            maxHeight: 300,
            overflow: "auto",
            fontSize: "0.75rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {error.stack}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(error.stack ?? error.message).then(() => {
                this.setState({ copied: true });
                setTimeout(() => this.setState({ copied: false }), 2000);
              });
            }}
          >
            {this.state.copied ? "Copied!" : "Copy stacktrace"}
          </Button>
          <Button variant="contained" onClick={() => this.setState({ error: null, copied: false })}>
            Try again
          </Button>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;
